import React, { useMemo } from 'react';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { useForm } from '.';
import type { FormErrorType } from './DefaultError';
import { getFormItemLayoutClasses } from './formItemLayout';
import { Tooltip } from '../Tooltip';

type FormItemProps<T, K> = (K extends StringKeyOf<T>
  ? {
      name: K;
      validate?: (value: T[K], values: T) => FormErrorType;
    }
  : never) & {
  tooltip?: string;
  inline?: boolean;
  required?: boolean;
  className?: string;
  label: React.ReactNode;
  labelClassName?: string;
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
  labelContainerClassName?: string;
  children: React.ReactElement<{ onChange?: Function; error?: any }>;
  showAsError?: boolean;
  renderError?: null | ((error: FormErrorType) => React.ReactNode);
  errorBelowInput?: boolean;
  compactInline?: boolean;
};

export const FormItem = <
  T extends {} = any,
  K extends keyof T & string = keyof T & string,
>({
  children,
  name,
  label,
  className,
  labelClassName = 'text-medium',
  tooltip,
  tooltipPosition = 'bottom',
  required,
  validate,
  inline = true,
  showAsError = false,
  labelContainerClassName,
  renderError: ourRenderError,
  errorBelowInput = false,
  compactInline = false,
  ...rest
}: FormItemProps<T, K>): React.JSX.Element => {
  const {
    getValue,
    getErrors,
    onChange,
    register,
    unregister,
    renderError: defaultRenderError,
    getTouched,
    setTouched,
  } = useForm<T>();

  const value = getValue?.()[name];
  const error = getErrors?.()[name];
  const isTouched = getTouched?.()[name];

  const showError = useMemo(
    () => value !== undefined && value !== null && isTouched,
    [value, isTouched]
  );
  const renderError = ourRenderError ?? defaultRenderError;
  const renderedError =
    !!renderError && !!error && showError && renderError(error);

  const layout = useMemo(
    () =>
      getFormItemLayoutClasses({
        inline,
        compactInline,
        labelContainerClassName,
      }),
    [inline, compactInline, labelContainerClassName]
  );

  const handleChange = (newValue: any | undefined) => {
    setTouched?.((prev: Record<StringKeyOf<T>, boolean>) => ({
      ...prev,
      [name]: true,
    }));
    onChange?.({ ...getValue?.(), [name]: newValue } as T);
    children?.props?.onChange?.(newValue);
  };

  React.useEffect(() => {
    register?.(name, { required, validate });
    return () => {
      unregister?.(name);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, required]);

  const isErrored = (!!renderedError || !!showAsError) && showError;

  return (
    <div className={clsx(className, 'flex', layout.root)}>
      {!!label && (
        <div
          className={clsx(
            twMerge(
              'text-left inline-flex items-center gap-x-1',
              layout.labelContainer,
              labelClassName
            ),
            inline && 'text-small'
          )}
        >
          {label}
          {!!required && <span className="text-danger">*</span>}
          {!!tooltip && (
            <Tooltip content={tooltip} position={tooltipPosition} />
          )}
        </div>
      )}
      <div
        className={clsx(
          layout.inputWrapper,
          errorBelowInput && 'flex flex-col gap-y-2'
        )}
      >
        <div
          className={clsx(
            layout.inputInner,
            isErrored && 'outline outline-danger outline-1 rounded-bt'
          )}
        >
          {React.cloneElement(children, {
            ...rest,
            value,
            error: showError ? error : undefined,
            onChange: handleChange,
          })}
        </div>
        {!!renderedError && (
          <div
            className={clsx(
              'text-danger text-left text-small italic mt-1 w-full break-words',
              layout.reserveInlineErrorSpace &&
                !errorBelowInput &&
                'min-h-[4rem]'
            )}
          >
            {renderedError}
          </div>
        )}
      </div>
    </div>
  );
};
